import { useEffect, useRef, useState } from "react";
import { supabase, functionErrorMessage } from "../lib/supabase.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { formatDateTime } from "../lib/datetime.js";
import { Button, Input } from "../lib/ui.js";
import { PHOTO_ACCEPT, photoErrorMessage, shrinkPhoto } from "../lib/jobPhotos.js";

interface PhotoRow {
  id: string;
  storage_path: string;
  source: "owner" | "customer";
  created_at: string;
}

/**
 * A job's photos (migration 0088): the owner adds some, or texts the customer a link to send their own, and can delete any photo whenever
 * they choose. The files are private; each thumbnail opens through a short-lived signed link. Every change goes through the job-photos Edge Function.
 */
export function JobPhotos({ jobId, contactPhone, readOnly }: { jobId: string; contactPhone: string | null; readOnly: boolean }) {
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState(contactPhone ? formatAuPhoneForDisplay(contactPhone) : "");
  const [link, setLink] = useState<{ url: string; note: string } | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase.from("job_photos").select("id, storage_path, source, created_at").eq("job_id", jobId).order("created_at");
    const rows = (data ?? []) as PhotoRow[];
    setPhotos(rows);
    if (rows.length > 0) {
      const { data: signed } = await supabase.storage.from("job-photos").createSignedUrls(
        rows.map((r) => r.storage_path),
        3600
      );
      const map: Record<string, string> = {};
      for (const s of signed ?? []) if (s.signedUrl && s.path) map[s.path] = s.signedUrl;
      setUrls(map);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  async function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    for (const file of [...files]) {
      const form = new FormData();
      form.append("action", "owner_upload");
      form.append("job_id", jobId);
      form.append("file", await shrinkPhoto(file));
      const { error: err } = await supabase.functions.invoke("job-photos", { body: form });
      if (err) {
        setError(photoErrorMessage(await functionErrorMessage(err)));
        break;
      }
    }
    setBusy(false);
    if (picker.current) picker.current.value = "";
    await load();
  }

  async function remove(photo: PhotoRow) {
    if (!window.confirm("Delete this photo? It is removed for good.")) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.functions.invoke("job-photos", { body: { action: "delete", photo_id: photo.id } });
    setBusy(false);
    if (err) return setError(photoErrorMessage(await functionErrorMessage(err)));
    await load();
  }

  async function requestLink(text: boolean) {
    setBusy(true);
    setError(null);
    setLink(null);
    const { data, error: err } = await supabase.functions.invoke("job-photos", { body: { action: "request_link", job_id: jobId, ...(text ? { to: phone } : {}) } });
    setBusy(false);
    if (err) return setError(photoErrorMessage(await functionErrorMessage(err)));
    const r = data as { url: string; text: string; days?: number };
    const note =
      r.text === "queued"
        ? `A text with the link is on its way to ${phone}.`
        : r.text === "texts_not_available"
          ? "Text messages are not switched on for your business, so nothing was sent. Copy the link and send it yourself."
          : r.text === "not_a_mobile"
            ? "That is not an Australian mobile number, so nothing was sent. Copy the link and send it yourself."
            : "Here is a link you can send.";
    setLink({ url: r.url, note: `${note} It works for ${r.days ?? 7} days.` });
  }

  return (
    <div style={{ borderTop: "1px solid var(--rc-border)", marginTop: 18, paddingTop: 14 }}>
      <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Photos</h3>
      {photos.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "0 0 8px" }}>No photos yet.</p>
      ) : (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          {photos.map((p) => (
            <figure key={p.id} style={{ margin: 0, width: 120 }}>
              <a href={urls[p.storage_path]} target="_blank" rel="noreferrer" aria-label="Open photo">
                {urls[p.storage_path] ? (
                  <img src={urls[p.storage_path]} alt="Job photo" style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 6, border: "1px solid var(--rc-border)", display: "block" }} />
                ) : (
                  <div style={{ width: 120, height: 90, borderRadius: 6, border: "1px solid var(--rc-border)", display: "grid", placeItems: "center", fontSize: 12, color: "var(--rc-text-faint)" }}>Photo</div>
                )}
              </a>
              <figcaption style={{ fontSize: 11.5, color: "var(--rc-text-muted)", marginTop: 2 }}>
                {p.source === "customer" ? "From customer" : "Added"} {formatDateTime(p.created_at)}
              </figcaption>
              {!readOnly && (
                <Button variant="ghost" disabled={busy} onClick={() => void remove(p)} style={{ padding: 0, fontSize: 12 }}>
                  Delete
                </Button>
              )}
            </figure>
          ))}
        </div>
      )}
      {!readOnly && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <input ref={picker} type="file" accept={PHOTO_ACCEPT} multiple hidden onChange={(e) => void addFiles(e.target.files)} />
            <Button disabled={busy} onClick={() => picker.current?.click()}>
              Add photos
            </Button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Input aria-label="Mobile to text the photo link to" placeholder="Customer's mobile" value={phone} maxLength={20} inputMode="tel" onChange={(e) => setPhone(e.target.value)} style={{ width: 170 }} />
            <Button disabled={busy || !phone.trim()} onClick={() => void requestLink(true)}>
              Text a photo link
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => void requestLink(false)}>
              Get a link only
            </Button>
          </div>
          {link && (
            <div style={{ fontSize: 12.5 }}>
              <p style={{ margin: "0 0 4px" }}>{link.note}</p>
              <Input aria-label="Photo link" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} />
            </div>
          )}
        </div>
      )}
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
}
